import React from 'react'
import { DropdownDivider, DropdownItem, DropdownLabel, DropdownMenu } from '../catalyst/dropdown'
import { Switch } from '../catalyst/switch'
import {
  ArrowRightStartOnRectangleIcon,
  IdentificationIcon,
  MoonIcon,
  UserIcon,
} from '@heroicons/react/16/solid'

type DropdownAnchor = React.ComponentProps<typeof DropdownMenu>['anchor']

interface UserMenuProps {
  profileUrl: string
  signOutUrl: string
  authenticityToken: string
  anchor?: DropdownAnchor
  /** Omitted where the app has no membership card route for this user. */
  memberCardUrl?: string | null
  /** Omitted on surfaces that expose their own standalone theme control. */
  darkMode?: { isDark: boolean; onChange: (dark: boolean) => void }
  /** Extra entries rendered above the sign-out divider, e.g. the dev user switcher. */
  children?: React.ReactNode
}

/**
 * The one user dropdown. Both navs mount it behind their own trigger, so the
 * entries and their labels stay identical wherever the avatar is clicked.
 */
export function UserMenu({
  profileUrl,
  signOutUrl,
  authenticityToken,
  anchor = 'bottom end',
  memberCardUrl,
  darkMode,
  children,
}: UserMenuProps) {
  return (
    <DropdownMenu className="min-w-64" anchor={anchor}>
      <DropdownItem href={profileUrl}>
        <UserIcon />
        <DropdownLabel>My profile</DropdownLabel>
      </DropdownItem>
      {memberCardUrl && (
        <DropdownItem href={memberCardUrl}>
          <IdentificationIcon />
          <DropdownLabel>Membership Card</DropdownLabel>
        </DropdownItem>
      )}
      {darkMode && (
        <DropdownItem as="div">
          <MoonIcon />
          <span className="col-start-2 row-start-1 text-base/6 text-[color:var(--theme-fg)] sm:text-sm/6 font-medium">
            Dark mode
          </span>
          {/*
                      The adjacent text is not associated with the control, and this
                      menu renders twice on a dashboard page (navbar and sidebar
                      footer), so an id-based `aria-labelledby` would collide.
                    */}
          <Switch aria-label="Dark mode" checked={darkMode.isDark} onChange={darkMode.onChange} />
        </DropdownItem>
      )}
      {children}
      <DropdownDivider />
      <form action={signOutUrl} method="post" className="contents">
        <input type="hidden" name="_method" value="delete" />
        <input type="hidden" name="authenticity_token" value={authenticityToken} />
        <DropdownItem as="button" type="submit">
          <ArrowRightStartOnRectangleIcon />
          <DropdownLabel>Sign out</DropdownLabel>
        </DropdownItem>
      </form>
    </DropdownMenu>
  )
}
